import { activeLocale, translateUiText } from '../i18n.js';
import { INSTITUTION_TIERS } from '../../shared/institution-data.js';
import React, { useEffect, useState } from 'react';
import {
  Activity, AlertTriangle, ArrowLeft, BadgeCheck, BarChart3, BrainCircuit,
  Bookmark, BriefcaseBusiness, Building2, CalendarDays, CalendarPlus, Check, ChevronRight, CircleHelp, CirclePlay, Clock3, ExternalLink, FileText,
  ContactRound, Globe2, GraduationCap, Languages, Layers3, Link2, Mail, MessageSquareText, Phone, RefreshCw, Scale, Send, Sparkles, Undo2, UserCheck, XCircle,
  Settings2,
} from 'lucide-react';
import { api } from '../api.js';
import {
  Avatar, Badge, Button, Callout, Confidence, EmptyState, Modal, ScoreRing,
  SectionHeader, Skeleton,
} from '../components/ui.jsx';
import {
  formatDate, getCandidateConfidence, getCandidateRecommendation, getCandidateScore, getResumeCareerSpan,
  getResumeContact, getResumeLanguages, getResumeRegions,
  isDesignJobTitle, scoreTone, statusLabel, workflowStatusLabel, workflowStatusTone,
} from '../utils.js';
import {
  buildResumeStructureIndex, cleanResumeDisplayText, companyBusinessTypes, companyOverviewText,
  groupSkillBlocks, looksLikeCompanyDescription, matchCompanyBackground, prepareResumeSections,
  resumeCompanyLine, resumeEntryLine,
} from '../resume-formatting.js';

const TABS = [
  { id: 'overview', label: '评估总览', icon: BarChart3 },
  { id: 'resume', label: '简历文本', icon: Languages },
  { id: 'career', label: '职业路径', icon: BriefcaseBusiness },
  { id: 'history', label: '人工记录', icon: MessageSquareText },
];

const COMPANY_BUSINESS_LABELS = {
  DTC: 'DTC', B2C: 'B2C', B2B: 'B2B', Marketplace: 'Marketplace', Agency: 'Agency',
  Offline: '线下为主', Omnichannel: '全渠道', Distribution: '分销/经销',
  Manufacturing: '制造', Unknown: '业务类型未知',
};
const COMPANY_SCALE_LABELS = {
  micro: '微型', small: '小型', medium: '中型', large: '大型', enterprise: '集团/企业级', unknown: '规模未知',
};
const COMPANY_BASIS_LABELS = {
  resume_explicit: '简历明示', business_description_inference: '业务描述推断',
  widely_known: '广泛已知', web_verified: '联网核验', web_inferred: '联网有限推断',
  ambiguous: '同名待确认', unknown: '待核实',
};

function DimensionRows({ dimensions = [] }) {
  return (
    <div className="dimension-list">
      {dimensions.map((dimension) => (
        <div className="dimension-row" key={dimension.key}>
          <div><strong>{dimension.label}</strong><span>权重 {dimension.weight}% · {dimension.evidenceCount ?? dimension.evidence?.length ?? 0} 条证据</span></div>
          <div className="dimension-track"><i style={{ width: `${dimension.score}%` }} /></div>
          <strong>{Math.round(dimension.score)}</strong>
          <span className="dimension-confidence" title={`置信度 ${Math.round(dimension.confidence * 100)}%`}>{Math.round(dimension.confidence * 100)}%</span>
        </div>
      ))}
    </div>
  );
}

function RiskList({ risks = [], pending = false }) {
  if (!risks.length) return pending
    ? <div className="no-risk"><CircleHelp size={20} /><div><strong>风险尚未评估</strong><span>文件解析与评分完成后，这里会显示需要人工确认的问题</span></div></div>
    : <div className="no-risk"><BadgeCheck size={20} /><div><strong>未发现高优先级风险</strong><span>仍建议在面试中验证关键证据</span></div></div>;
  return (
    <div className="risk-list">
      {risks.map((risk, index) => (
        <article key={`${risk.title}-${index}`} className={`risk-item risk-${risk.severity}`}>
          <span><AlertTriangle size={18} /></span>
          <div><div><Badge tone={risk.severity === 'high' ? 'danger' : risk.severity === 'medium' ? 'warning' : 'neutral'}>{risk.type}</Badge><strong>{risk.title}</strong></div><p>{risk.detail}</p><div className="interview-question-pair"><div><span>中文问题</span><p><CircleHelp size={15} />{risk.question}</p></div><div><span>Interview question</span><p lang="en"><MessageSquareText size={15} />{risk.questionEn || legacyEnglishInterviewQuestion(risk)}</p></div></div></div>
        </article>
      ))}
    </div>
  );
}

function legacyEnglishInterviewQuestion(risk = {}) {
  const value = `${risk.type || ''} ${risk.title || ''} ${risk.question || ''}`.toLocaleLowerCase();
  if (/重叠|并行|overlap|concurrent/.test(value)) return 'Please clarify the overlapping employment dates, the nature of each engagement, and the weekly time commitment for every current role.';
  if (/timeline|时间线|任职时间|日期|current status|开始日期|结束日期/.test(value)) return 'Please confirm the actual start date, current employment status, and what the stated end date means for this role.';
  if (/outcome|转化|量化|基线|指标|cvr|ctr/.test(value)) return 'What were the baseline, measurement period, and key metrics before and after this work, and which changes can reasonably be attributed to your contribution?';
  if (/experiment|a\/b|实验|假设|样本|显著性/.test(value)) return 'Please walk us through one experiment, including the hypothesis, version differences, primary metric, sample period, result, uncertainty, and the next iteration.';
  if (/tooling|工具|pagefly|theme editor|ga4|热力图|录屏/.test(value)) return 'Which ecommerce, page-building, and behavior-analysis tools have you used, and how did you take one finding from data through launch and post-launch review?';
  if (/年限|完整时间线|全部.*经历|experience requirement/.test(value)) return 'Please walk us through your complete UI/UX experience timeline, including the dates and your responsibilities for website, landing-page, ecommerce, or mobile-web projects.';
  if (/platform|amazon|shopify|平台/.test(value)) return 'Please describe your most recent hands-on work on the relevant ecommerce platform, including the market, account scope, actions taken, and measurable results.';
  if (/career|level|职级|层级|岗位/.test(value)) return 'Why are you considering this role and level now, and how do its reporting line, hands-on workload, and growth path fit your expectations?';
  if (/data|metric|数据|指标|口径/.test(value)) return 'Please walk us through one analysis from the original data and baseline to your diagnosis, decision, personal contribution, and measurable outcome.';
  if (/cro|skill|能力|技能|evidence/.test(value)) return 'Please provide a specific example that demonstrates this capability, including the problem, hypothesis, actions, validation method, and result.';
  return 'Please clarify this concern with a specific example, including the context, your role, actions, evidence, and measurable outcome.';
}

function PotentialPanel({ candidate, locale }) {
  const potential = candidate.potential || {};
  return (
    <>
      <SectionHeader
        title="潜力识别"
        description="与当前岗位胜任度分开判断"
        action={<div className="potential-detail-score"><span><BrainCircuit size={14} />潜力分</span><strong>{potential.score ?? '—'}</strong></div>}
      />
      <div className="potential-detail">
        <div className="potential-detail-content" data-i18n-ignore>
          <Badge tone="neutral">{potential.level || translateUiText('待评估', locale)}</Badge>
          <p>{potential.evidence || translateUiText('当前证据不足，等待人工观察。', locale)}</p>
          <div>{potential.signals?.map((signal) => <Badge key={signal} tone="info"><Sparkles size={12} />{signal}</Badge>)}</div>
        </div>
      </div>
    </>
  );
}

function resumeSourceText(candidate) {
  return (candidate.assets || [])
    .filter((asset) => asset.kind.startsWith('resume'))
    .map((asset) => {
      const extraction = asset.extraction || {};
      const visionText = (extraction.visionOcr || [])
        .filter((page) => page?.text?.trim())
        .sort((a, b) => Number(a.pageNumber || 0) - Number(b.pageNumber || 0))
        .map((page) => page.text.trim())
        .join('\n\n');
      // Image-only PDFs often produce noisy Tesseract output for columns,
      // icons and tinted backgrounds. Prefer the reviewed visual transcript
      // only when no real PDF text layer exists.
      const text = extraction.text?.trim() || visionText || extraction.ocrText || '';
      return text.trim() ? `## ${asset.originalName}\n${text.trim()}` : '';
    })
    .filter(Boolean)
    .join('\n\n');
}

function profileDateRange(item) {
  const end = item.endDate || item.graduationDate;
  return [item.startDate, end].filter(Boolean).join(' - ') || '时间未识别';
}

const LEGACY_NOTABLE_COMPANIES = new Map([
  ['deloitte', '全球四大会计师事务所之一'], ['pwc', '全球四大会计师事务所之一'],
  ['ey', '全球四大会计师事务所之一'], ['kpmg', '全球四大会计师事务所之一'],
  ['google', '全球头部科技公司'], ['meta', '全球头部科技公司'], ['amazon', '全球头部电商与科技公司'],
  ['microsoft', '全球头部科技公司'], ['apple', '全球头部消费电子与科技公司'],
  ['bytedance', '全球头部互联网平台'], ['tiktok', '全球头部社交内容平台'],
  ['tencent', '中国头部互联网平台'], ['alibaba', '中国头部电商与科技公司'],
  ['shopify', '全球头部电商平台'], ['shopee', '东南亚头部电商平台'], ['lazada', '东南亚头部电商平台'],
  ['nike', '全球头部运动品牌'], ['adidas', '全球头部运动品牌'], ['unilever', '全球头部消费品公司'],
  ['procter & gamble', '全球头部消费品公司'], ['p&g', '全球头部消费品公司'],
  ['dyson', '全球知名消费科技公司'], ['samsung', '全球头部消费电子公司'], ['huawei', '全球头部通信与消费电子公司'],
]);

function legacyNotabilityReason(company = '') {
  const normalized = company.toLocaleLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();
  for (const [knownCompany, reason] of LEGACY_NOTABLE_COMPANIES) {
    if (normalized === knownCompany || normalized.startsWith(`${knownCompany} `)) return reason;
  }
  return '';
}

function legacyMajorIsRelevant(major = '', jobTitle = '') {
  const value = `${major} ${jobTitle}`.toLocaleLowerCase();
  if (/ui\s*\/\s*ux|uiux|产品设计师|交互设计师/.test(jobTitle.toLocaleLowerCase())) {
    return /interaction design|human.?computer|user experience|ux design|product design|digital design|交互设计|用户体验|产品设计|数字媒体|视觉传达/.test(value);
  }
  if (/工业设计|industrial design|人体工学/.test(jobTitle.toLocaleLowerCase())) {
    return /industrial design|product design|ergonom|mechanical|工业设计|产品设计|人体工学|机械/.test(value);
  }
  if (/电商|营销|广告|社媒|pr|marketing/.test(jobTitle.toLocaleLowerCase())) {
    return /marketing|commerce|business|communication|advertising|市场|营销|传播|电子商务|商业/.test(value);
  }
  return false;
}

function assessmentModelLabel(candidate) {
  const scoring = candidate.modelIds?.scoring;
  const scoringRun = (candidate.aiRuns || []).find((run) => (
    run.id === scoring?.runId && run.purpose === 'candidate_scoring' && run.status === 'completed'
  )) || (candidate.aiRuns || []).find((run) => (
    run.purpose === 'candidate_scoring' && run.status === 'completed'
  ));
  const provider = scoringRun?.providerName;
  const model = scoringRun?.modelId || (typeof scoring === 'string' ? scoring : scoring?.model);
  return [provider, model].filter(Boolean).join(' / ');
}

function normalizedResumeProfile(candidate) {
  const profile = candidate.resumeProfile || {};
  return {
    education: (profile.education || []).map((item) => {
      const industryRelevant = item.industryRelevant === true
        || (item.industryRelevant === undefined && legacyMajorIsRelevant(item.major, candidate.jobTitle));
      return {
        ...item,
        industryRelevant,
        relevanceNote: item.relevanceNote || (industryRelevant ? `该专业与${candidate.jobTitle || '目标岗位'}的核心能力直接相关` : ''),
      };
    }),
    notableCompanies: (profile.notableCompanies || []).map((item) => ({
      ...item,
      notabilityReason: item.notabilityReason || legacyNotabilityReason(item.company),
    })).filter((item) => item.company && item.notabilityReason),
    languages: getResumeLanguages(candidate),
    contact: getResumeContact(candidate),
  };
}

const INSTITUTION_TYPE_LABELS = {
  comprehensive_university: '综合性大学',
  technical_university: '理工类院校',
  normal_university: '师范类院校',
  arts_academy: '艺术类院校',
  business_school: '商科院校',
  medical_university: '医药类院校',
  agricultural_university: '农林类院校',
  language_university: '外国语类院校',
  law_university: '政法类院校',
  sports_university: '体育类院校',
  polytechnic: '理工学院',
  vocational_institute: '职业技术院校',
};

function institutionTypeLabel(type) {
  return translateUiText(INSTITUTION_TYPE_LABELS[type] || '院校类型未知', activeLocale());
}

// "Polytechnic" beside "SG Polytechnic" says one thing twice, so a type a tier
// already states is not repeated.
function typeStatedByTier(institution) {
  if (!institution?.type) return true;
  return (institution.tiers || []).some((tier) => INSTITUTION_TIERS[tier]?.impliesType === institution.type);
}

// Neutral on purpose: a rank rendered into a sentence cannot be translated,
// because the finished string is not a key the dictionary holds.
function institutionRankLine(institution) {
  if (!institution?.qs) return '';
  return `${institution.qs.source} ${institution.qs.year} · #${institution.qs.rank}`;
}

function ResumeProfilePanel({ candidate }) {
  const { education, notableCompanies: companies, languages, contact } = normalizedResumeProfile(candidate);
  const regions = getResumeRegions(candidate);
  const careerSpan = getResumeCareerSpan(candidate);
  return (
    <section className="panel resume-profile-panel">
      <SectionHeader title="简历基本概况" description="仅展示简历原文明确支持的信息；悬停可查看抽取证据" />
      <div className="resume-profile-grid">
        <article>
          <span><GraduationCap size={18} /></span>
          <div><h3>教育背景</h3>{education.length ? education.map((item, index) => (
            <div className="profile-fact" key={`${item.school}-${index}`} title={item.evidence || ''}>
              <div className="profile-fact-title"><strong>{item.school || '院校未识别'}</strong>{item.industryRelevant && <Badge tone="success">岗位相关专业</Badge>}</div>
              {item.institution && (
                <ul className="institution-tags">
                  {!typeStatedByTier(item.institution) && <li className="institution-type">{institutionTypeLabel(item.institution.type)}</li>}
                  {item.institution.badges.map((badge) => <li key={badge}>{badge}</li>)}
                </ul>
              )}
              <p>{[item.degree, item.major].filter(Boolean).join(' · ') || '学历 / 专业未识别'}</p>
              {item.institution?.qs && (
                <small className="institution-source">{institutionRankLine(item.institution)}</small>
              )}
              {item.industryRelevant && item.relevanceNote && <p className="profile-fact-note">{item.relevanceNote}</p>}
              <small><CalendarDays size={12} />{profileDateRange(item)}</small>
            </div>
          )) : <div className="profile-fact empty"><strong>未识别</strong><p>重新评估后从简历原文抽取</p></div>}</div>
        </article>
        <article>
          <span><Building2 size={18} /></span>
          <div><h3>知名公司经历</h3>{companies.length ? companies.map((item, index) => (
            <div className="profile-fact" key={`${item.company}-${index}`} title={item.evidence || ''}>
              <strong>{item.company || '公司未识别'}</strong>
              <p>{item.role || '职位未识别'}</p>
              <p className="profile-fact-note">{item.notabilityReason}</p>
              <small><CalendarDays size={12} />{profileDateRange(item)}</small>
            </div>
          )) : <div className="profile-fact empty"><strong>无明确记录</strong><p>仅展示有广泛知名度依据的任职公司；普通公司和客户品牌不会列入</p></div>}</div>
        </article>
        <article>
          <span><Languages size={18} /></span>
          <div><h3>语言能力</h3>{languages.length ? <div className="profile-language-list">{languages.map((item, index) => (
            <Badge key={`${item.language}-${index}`} tone="info" title={item.evidence || ''}>{item.language || '语言未识别'}{item.proficiency ? ` · ${item.proficiency}` : ''}</Badge>
          ))}</div> : <div className="profile-fact empty"><strong>未识别</strong><p>未明确写出的熟练度不会推断</p></div>}</div>
        </article>
        <article>
          <span><ContactRound size={18} /></span>
          <div><h3>联系方式</h3>{contact.email || contact.phone ? <div className="profile-contact-list" title={contact.evidence || ''}>
            {contact.email && <a href={`mailto:${contact.email}`}><Mail size={14} /><span><small>邮箱</small><strong>{contact.email}</strong></span></a>}
            {contact.phone && <a href={`tel:${contact.phone.replace(/[^+\d]/g, '')}`}><Phone size={14} /><span><small>电话</small><strong>{contact.phone}</strong></span></a>}
          </div> : <div className="profile-fact empty"><strong>未识别</strong><p>仅展示简历中明确写出的邮箱和电话</p></div>}</div>
        </article>
        <article>
          <span><Globe2 size={18} /></span>
          <div><h3>地区与市场经历</h3>{regions.length ? <div className="profile-language-list">{regions.map((item) => <Badge key={item.label} tone="neutral" title={item.evidence}>{item.label}</Badge>)}</div> : <div className="profile-fact empty"><strong>未识别</strong><p>只展示简历明确出现的学习、工作或市场地区，不推断国籍</p></div>}</div>
        </article>
        <article>
          <span><BriefcaseBusiness size={18} /></span>
          <div><h3>职业资历</h3>{careerSpan ? <div className="profile-fact" title={careerSpan.evidence}><strong>{careerSpan.years} 年明确职业跨度</strong><p>最早记录始于 {careerSpan.startYear} 年</p><small>不用于推断年龄</small></div> : <div className="profile-fact empty"><strong>未识别</strong><p>缺少可确认的工作起始年份</p></div>}</div>
        </article>
      </div>
    </section>
  );
}

const languageLabel = (value) => ({ zh: '中文', 'zh-CN': '中文', en: '英文', English: '英文', Chinese: '中文' }[value] || value || '未识别');

// The supplemental-information strip sits away from the education entry, so it
// repeats the tier and rank together instead of styling the school name itself.
function institutionNote(institution) {
  if (!institution) return '';
  return [(institution.badges || []).join(' · '), institutionRankLine(institution)]
    .filter(Boolean).join(' · ');
}

function resumeAnnotations(candidate) {
  const profile = normalizedResumeProfile(candidate);
  const annotations = [];
  const seen = new Set();
  const add = (annotation) => {
    const key = `${annotation.type}:${annotation.term.toLocaleLowerCase()}`;
    if (annotation.term.trim().length < 2 || seen.has(key)) return;
    seen.add(key);
    annotations.push(annotation);
  };
  (profile.notableCompanies || []).forEach((item) => {
    if (item.company && item.notabilityReason) add({ type: 'company', term: item.company.trim(), label: '知名公司', note: item.notabilityReason.trim() });
  });
  (profile.education || []).forEach((item) => {
    if (item.industryRelevant && item.major) add({ type: 'major', term: item.major.trim(), label: '岗位相关专业', note: item.relevanceNote?.trim() || '该专业背景与目标岗位相关' });
    const note = institutionNote(item.institution);
    if (item.school && note) add({ type: 'school', term: item.school.trim(), label: '院校层级', note });
  });
  return annotations;
}

function ResumeInlineText({ text, lineKey }) {
  return String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, index) => {
    const isStrong = /^\*\*[^*]+\*\*$/.test(part);
    const clean = isStrong ? part.slice(2, -2) : part;
    return isStrong ? <strong key={`${lineKey}-strong-${index}`}>{clean}</strong> : <React.Fragment key={`${lineKey}-plain-${index}`}>{clean}</React.Fragment>;
  });
}

function ResumeLineContent({ text, lineKey }) {
  const entry = resumeEntryLine(text);
  if (!entry) return <ResumeInlineText text={text} lineKey={lineKey} />;
  return <span className="resume-entry-line"><strong><ResumeInlineText text={entry.title} lineKey={`${lineKey}-title`} /></strong><span className="resume-entry-date"><ResumeInlineText text={entry.date} lineKey={`${lineKey}-date`} /></span></span>;
}

function ResumeCompanyLine({ company, country, lineKey }) {
  return <span className="resume-entry-line resume-company-line"><strong><ResumeInlineText text={company} lineKey={`${lineKey}-company`} /></strong><span className="resume-entry-country"><Globe2 size={12} /><ResumeInlineText text={country} lineKey={`${lineKey}-country`} /></span></span>;
}

function renderResumeLines(lines, sectionIndex) {
  const nodes = [];
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    const line = lines[lineIndex];
    const lineKey = `resume-${sectionIndex}-${lineIndex}`;
    if (line.kind === 'spacer') {
      nodes.push(<span className="resume-spacer" aria-hidden="true" key={lineKey} />);
      continue;
    }
    if (line.kind === 'bullet') {
      nodes.push(<div className="resume-bullet" key={lineKey}><span>{line.marker}</span><p><ResumeInlineText text={line.text} lineKey={lineKey} /></p></div>);
      continue;
    }
    // A company, its short business description and the following dated role
    // are one employment entry. Keeping them in one wrapper prevents the
    // company note from looking like a separate sibling block.
    if (line.kind === 'company-entry') {
      const note = lines[lineIndex + 1]?.kind === 'company-note' ? lines[lineIndex + 1] : null;
      const role = lines[lineIndex + 2];
      const roleEntry = role && resumeEntryLine(role.text);
      if (note && roleEntry) {
        nodes.push(
          <article className="resume-experience-entry" key={lineKey}>
            <p className="resume-entry-paragraph resume-company-entry"><ResumeCompanyLine company={line.company} country={line.country} lineKey={lineKey} /></p>
            <p className="resume-entry-note"><ResumeInlineText text={note.text} lineKey={`${lineKey}-note`} /></p>
            <p className="resume-entry-role"><ResumeLineContent text={role.text} lineKey={`${lineKey}-role`} /></p>
          </article>,
        );
        lineIndex += 2;
        continue;
      }
      nodes.push(<p className="resume-entry-paragraph resume-company-entry" key={lineKey}><ResumeCompanyLine company={line.company} country={line.country} lineKey={lineKey} /></p>);
      continue;
    }
    // These two lines are consumed by the employment-entry branch above.
    if (line.kind === 'company-note') continue;
    nodes.push(<p className={`${line.kind === 'lead' ? 'resume-line-lead ' : ''}${resumeEntryLine(line.text) ? 'resume-entry-paragraph' : ''}`} key={lineKey}><ResumeLineContent text={line.text} lineKey={lineKey} /></p>);
  }
  return nodes;
}

function ResumeDocumentBody({ text }) {
  const sections = prepareResumeSections(text);
  return (
    <div className="resume-document-body">
      {sections.map((section, sectionIndex) => (
        <section className="resume-section" key={`${section.title}-${sectionIndex}`}>
          {section.title && <h3><ResumeInlineText text={section.title} lineKey={`heading-${sectionIndex}`} /></h3>}
          <div className="resume-section-lines">
            {renderResumeLines(section.lines, sectionIndex)}
          </div>
        </section>
      ))}
    </div>
  );
}

function groupBulletBlocks(blocks = []) {
  const groups = [];
  for (const block of blocks) {
    if (block.type === 'bullet') {
      const previous = groups.at(-1);
      if (previous?.type === 'bullet-list') previous.items.push(block);
      else groups.push({ type: 'bullet-list', items: [block] });
    } else {
      groups.push(block);
    }
  }
  return groups;
}

// PDF line extraction often stores one wrapped prose line per paragraph block.
// Education descriptions are single statements, so merge adjacent plain lines
// before rendering and let the card width determine the final wrapping.
function mergeEducationParagraphs(blocks = []) {
  const merged = [];
  for (const block of blocks) {
    const previous = merged.at(-1);
    if (previous?.type === 'paragraph' && block.type === 'paragraph'
      && !previous.label && !block.label && !previous.strong && !block.strong) {
      previous.text = `${previous.text} ${block.text}`.replace(/\s+/g, ' ').trim();
      previous.sourceLineEnd = block.sourceLineEnd || previous.sourceLineEnd;
    } else {
      merged.push({ ...block });
    }
  }
  return merged;
}

function inferredContactLabel(value = '') {
  const text = String(value).trim();
  if (/@/.test(text)) return 'Email';
  if (/^(?:\+?[\d()\s-]){7,}$/.test(text)) return 'Phone';
  if (/linkedin\.com/i.test(text)) return 'LinkedIn';
  if (/https?:\/\//i.test(text)) return 'Website';
  if (/\b(?:singapore|malaysia|china|india|indonesia|thailand|vietnam|philippines|hong kong|taiwan)\b/i.test(text)) return 'Location';
  return 'Contact Detail';
}

function compactSemanticValues(value, canonical) {
  const values = String(value || '').split(/[•●▪◦;,]/).map((item) => item.trim()).filter(Boolean);
  if (canonical !== 'languages') return values;
  return values.flatMap((item) => {
    const languages = item.match(/\b(?:English|Mandarin|Chinese|Malay|Tamil|Cantonese|Hokkien|Japanese|Korean|French|German|Spanish|Hindi|Bahasa Indonesia|Bahasa Melayu)\b(?:\s*\([^)]*\))?/gi);
    return languages?.length > 1 ? languages.map((language) => language.trim()) : [item];
  });
}

// The pipeline already resolved each school against the registry, so the resume
// view reuses that answer instead of matching names a second time and risking a
// different one.
function matchInstitutionEntry(entry, details, education = []) {
  const haystack = [entry.title, ...(entry.meta || []), ...details.map((item) => item.text || item.title || '')]
    .filter(Boolean).join(' ').toLocaleLowerCase();
  if (!haystack) return null;
  return education.find((item) => item.institution && item.school
    && haystack.includes(String(item.school).toLocaleLowerCase())) || null;
}

function blockPlainText(block) {
  if (block.type === 'entry') return [block.title, ...(block.meta || [])].filter(Boolean).join(' ');
  return [block.label, block.text].filter(Boolean).join(': ');
}

function groupSemanticBlocks(blocks = [], canonical = '', companyBackgrounds = [], education = []) {
  if (canonical === 'contact') return [{ type: 'fact-grid', items: blocks }];
  if (canonical === 'skills') return groupSkillBlocks(blocks);
  if (['languages', 'certifications'].includes(canonical)) {
    const items = blocks.flatMap((block) => {
      if (canonical === 'certifications' && block.type === 'entry') {
        return [[block.title, ...(block.meta || [])].filter(Boolean).join(' ').replace(/\s+/g, ' ').trim()];
      }
      const values = block.type === 'entry'
        ? [block.title, ...(block.meta || [])]
        : [[block.label, block.text].filter(Boolean).join(': ')];
      return values.flatMap((value) => compactSemanticValues(value, canonical));
    });
    return [{ type: 'compact-grid', items }];
  }
  if (!['experience', 'education'].includes(canonical)) return groupBulletBlocks(blocks);
  const groups = [];
  let current = null;
  const flush = () => {
    if (!current) return;
    // Priority: company description written in the resume, then the AI company
    // background. Never promote a responsibility line into the note slot.
    let companySummary = current.entry.companySummary || null;
    let details = current.details;
    if (!companySummary && details[0]?.type === 'paragraph' && !details[0]?.strong
        && looksLikeCompanyDescription(details[0]?.text)) {
      companySummary = details[0].text;
      details = details.slice(1);
    }
    const background = canonical === 'experience'
      ? matchCompanyBackground(current.entry, companyBackgrounds) : null;
    if (!companySummary) companySummary = companyOverviewText(background);
    // A school is labelled here for the same reason an employer is: the reader is
    // looking at the resume, not at the profile panel.
    const schooling = canonical === 'education'
      ? matchInstitutionEntry(current.entry, details, education) : null;
    const institution = schooling?.institution || null;
    groups.push({
      ...current,
      entry: {
        ...current.entry,
        companySummary,
        businessTypes: companyBusinessTypes(background),
        institutionBadges: institution?.badges || [],
        institutionNote: institution ? institutionRankLine(institution) : '',
      },
      details: canonical === 'education'
        ? mergeEducationParagraphs(groupBulletBlocks(details))
        : groupBulletBlocks(details),
    });
    current = null;
  };
  // Every resume writes its education differently -- school first, degree first,
  // dated, undated, labelled beneath a heading -- and matching each shape with
  // another pattern only postpones the next one. The model has already read the
  // schools out of this resume, so those names anchor the entries instead.
  const schools = (education || [])
    .map((item) => String(item.school || '').trim().toLocaleLowerCase())
    .filter((name) => name.length >= 4);
  // The qualification, the subject and the years, as the assessment read them.
  // A promoted entry gets the same header as one the extractor built, so two
  // resumes do not present the same facts in two different places.
  const educationMeta = (item) => {
    const span = item.startDate && item.endDate
      ? `${item.startDate} - ${item.endDate}`
      : (item.graduationDate || item.endDate || item.startDate || '');
    return [item.degree, item.major, span].map((value) => String(value || '').trim()).filter(Boolean);
  };

  // Whatever the line said beyond those facts -- a grade, an honours class --
  // is still the resume's own text and stays with the entry.
  const leftoverAfter = (remainder, meta) => {
    let rest = remainder;
    for (const value of meta) rest = rest.split(value).join(' ');
    rest = rest.replace(/\s+/g, ' ').replace(/^[\s,;:，、：.·|-]+|[\s,;:，、：.·|-]+$/g, '').trim();
    rest = rest.replace(/^(?:in|of|at|from|and)\b\s*/i, '').trim();
    if (!/[\p{L}\p{N}]/u.test(rest)) return '';
    // What survives is often a scrap of the phrasing rather than a fact: the
    // "in" between a degree and its subject, or the acronym the degree already
    // spells out. Neither is worth a line of its own.
    if (/^(?:in|of|at|from|and|the)$/i.test(rest)) return '';
    if (meta.some((value) => value.toLocaleLowerCase().includes(rest.toLocaleLowerCase()))) return '';
    return rest;
  };

  // The school is the heading; the degree and year written around it on the same
  // line read as its detail, not as part of its name.
  const schoolEntryFrom = (block) => {
    if (canonical !== 'education' || !schools.length) return null;
    // An entry's meta is already separated out; only its title can still be
    // carrying the school buried among the qualification and the dates.
    const text = block.type === 'entry' ? String(block.title || '').trim() : blockPlainText(block);
    const haystack = text.toLocaleLowerCase();
    const matched = (education || []).find((item) => {
      const school = String(item.school || '').trim().toLocaleLowerCase();
      return school.length >= 4 && haystack.includes(school);
    });
    const name = matched && String(matched.school).trim().toLocaleLowerCase();
    if (!name) return null;
    const at = haystack.indexOf(name);
    // An entry whose title is already just the school is correct as it stands,
    // and rebuilding it would discard the qualification and dates it carries.
    if (block.type === 'entry' && text.trim().length === name.length) return null;
    const remainder = `${text.slice(0, at)} ${text.slice(at + name.length)}`
      .replace(/\s+/g, ' ').replace(/^[\s,;:，、：]+|[\s,;:，、：]+$/g, '').trim();
    const meta = block.type === 'entry' && (block.meta || []).length
      ? block.meta
      : educationMeta(matched);
    return {
      entry: {
        type: 'entry',
        title: text.slice(at, at + name.length),
        meta,
        sourceLineStart: block.sourceLineStart,
        sourceLineEnd: block.sourceLineEnd,
      },
      remainder: leftoverAfter(remainder, meta),
    };
  };

  for (const block of blocks) {
    const promoted = schoolEntryFrom(block);
    if (promoted) {
      flush();
      current = {
        type: 'experience-entry',
        entry: promoted.entry,
        details: promoted.remainder ? [{
          type: 'paragraph', text: promoted.remainder, strong: false,
          sourceLineStart: block.sourceLineStart, sourceLineEnd: block.sourceLineEnd,
        }] : [],
      };
    } else if (block.type === 'entry') {
      flush();
      current = { type: 'experience-entry', entry: block, details: [] };
    } else if (current) {
      current.details.push(block);
    } else {
      groups.push(block);
    }
  }
  flush();
  return groupBulletBlocks(groups);
}

function ResumeSemanticBlocks({ blocks, keyPrefix }) {
  return blocks.map((block, blockIndex) => {
    const blockKey = `${keyPrefix}-${blockIndex}`;
    if (block.type === 'bullet-list') return (
      <ul className="resume-semantic-list" key={blockKey}>
        {block.items.map((item, itemIndex) => (
          <li key={`${blockKey}-item-${itemIndex}`}>
            {item.label && <strong>{item.label}：</strong>}
            <ResumeInlineText text={item.text} lineKey={`${blockKey}-item-${itemIndex}`} />
          </li>
        ))}
      </ul>
    );
    if (block.type === 'fact-grid') return (
      <dl className="resume-semantic-facts" key={blockKey}>
        {block.items.map((item, itemIndex) => {
          const rawText = item.type === 'entry' ? item.title : item.text;
          const labeled = String(rawText || '').match(/^([^:：]{2,38})[:：]\s*(.+)$/);
          const label = item.label || labeled?.[1] || inferredContactLabel(rawText);
          const value = item.label ? item.text : labeled?.[2] || rawText;
          return <div key={`${blockKey}-fact-${itemIndex}`}>{label && <dt>{label}</dt>}<dd><ResumeInlineText text={value} lineKey={`${blockKey}-fact-${itemIndex}`} /></dd></div>;
        })}
      </dl>
    );
    if (block.type === 'compact-grid') return (
      <ul className="resume-semantic-compact-grid" key={blockKey}>
        {block.items.map((item, itemIndex) => <li key={`${blockKey}-compact-${itemIndex}`}><ResumeInlineText text={item} lineKey={`${blockKey}-compact-${itemIndex}`} /></li>)}
      </ul>
    );
    if (block.type === 'skill-group') return (
      <section className="resume-semantic-skill-group" key={blockKey}>
        <strong><ResumeInlineText text={block.label} lineKey={`${blockKey}-label`} /></strong>
        <ul className="resume-semantic-compact-grid">
          {block.items.map((item, itemIndex) => <li key={`${blockKey}-skill-${itemIndex}`}><ResumeInlineText text={item} lineKey={`${blockKey}-skill-${itemIndex}`} /></li>)}
        </ul>
      </section>
    );
    if (block.type === 'skill-detail') return (
      <article className="resume-semantic-skill-detail" key={blockKey}>
        <strong><ResumeInlineText text={block.label} lineKey={`${blockKey}-label`} /></strong>
        <p><ResumeInlineText text={block.text} lineKey={`${blockKey}-text`} /></p>
      </article>
    );
    if (block.type === 'experience-entry') return (
      <article className="resume-semantic-employment" key={blockKey}>
        <header>
          <strong><ResumeInlineText text={block.entry.title} lineKey={`${blockKey}-title`} /></strong>
          {block.entry.meta?.length > 0 && <div>{block.entry.meta.map((item, metaIndex) => (
            <span key={`${blockKey}-meta-${metaIndex}`}><ResumeInlineText text={item} lineKey={`${blockKey}-meta-${metaIndex}`} /></span>
          ))}</div>}
        </header>
        {(block.entry.companySummary || block.entry.businessTypes?.length > 0
          || block.entry.institutionBadges?.length > 0 || block.entry.institutionNote) && (
          <div className="resume-entry-note">
            {block.entry.businessTypes?.length > 0 && (
              <ul className="resume-entry-tags">
                {block.entry.businessTypes.map((type) => (
                  <li key={`${blockKey}-type-${type}`}>{type}</li>
                ))}
              </ul>
            )}
            {block.entry.institutionBadges?.length > 0 && (
              <ul className="resume-entry-tags resume-entry-tags-institution">
                {block.entry.institutionBadges.map((badge) => (
                  <li key={`${blockKey}-badge-${badge}`}>{badge}</li>
                ))}
              </ul>
            )}
            {block.entry.institutionNote && !block.entry.companySummary && (
              <p className="resume-entry-institution-note">{block.entry.institutionNote}</p>
            )}
            {block.entry.companySummary && (
              <p><ResumeInlineText text={block.entry.companySummary} lineKey={`${blockKey}-company-summary`} /></p>
            )}
          </div>
        )}
        {block.details.length > 0 && <div className="resume-semantic-employment-details"><ResumeSemanticBlocks blocks={block.details} keyPrefix={`${blockKey}-detail`} /></div>}
      </article>
    );
    if (block.type === 'entry') return (
      <article className="resume-semantic-entry" key={blockKey}>
        <strong><ResumeInlineText text={block.title} lineKey={`${blockKey}-title`} /></strong>
        {block.meta?.length > 0 && <div className="resume-semantic-entry-meta">{block.meta.map((item, metaIndex) => (
          <span key={`${blockKey}-meta-${metaIndex}`}><ResumeInlineText text={item} lineKey={`${blockKey}-meta-${metaIndex}`} /></span>
        ))}</div>}
      </article>
    );
    return (
      <p className={`resume-semantic-paragraph${block.strong ? ' resume-line-lead' : ''}`} key={blockKey}>
        {block.label && <strong>{block.label}：</strong>}
        <ResumeInlineText text={block.text} lineKey={blockKey} />
      </p>
    );
  });
}

function ResumeSemanticDocumentBody({ documents, companyBackgrounds = [], education = [] }) {
  const showAssetNames = documents.length > 1;
  return (
    <div className="resume-document-body resume-semantic-body">
      {documents.map(({ asset, structure }, documentIndex) => (
        <div className="resume-semantic-document" key={asset.id || `resume-document-${documentIndex}`}>
          {showAssetNames && <h2 className="resume-semantic-asset-name">{asset.originalName}</h2>}
          {structure.sections.map((section, sectionIndex) => (
            <section className="resume-semantic-section" key={`${section.canonical}-${section.title}-${sectionIndex}`}>
              <h3><ResumeInlineText text={section.title} lineKey={`semantic-heading-${documentIndex}-${sectionIndex}`} /></h3>
              <div className="resume-semantic-blocks">
                <ResumeSemanticBlocks
                  blocks={groupSemanticBlocks(section.blocks, section.canonical, companyBackgrounds, education)}
                  keyPrefix={`semantic-${documentIndex}-${sectionIndex}`}
                />
              </div>
            </section>
          ))}
        </div>
      ))}
    </div>
  );
}

function ResumeAnnotationStrip({ annotations }) {
  if (!annotations.length) return null;
  return (
    <aside className="resume-annotation-strip" aria-label="简历重点标注">
      <div className="resume-annotation-heading"><Sparkles size={15} /><span>重点标注</span></div>
      <div>{annotations.map((item) => (
        <span className={`resume-annotation-item annotation-${item.type}`} key={`${item.type}-${item.term}`}>
          <strong>{item.term}</strong><small>{item.label} · {item.note}</small>
        </span>
      ))}</div>
    </aside>
  );
}

// A translation is only what the reader wants when it is in the language they
// chose. Opening a Chinese translation for someone reading the interface in
// English hides the resume they asked for behind one they did not.
function translationSuitsInterface(translation) {
  const chinese = /^zh/i.test(String(translation?.targetLang || ''));
  return (activeLocale() === 'zh-CN') === chinese;
}

function ResumeTextTab({ candidate, newTranslationId }) {
  const translations = candidate.translations || [];
  const suitable = translations.find(translationSuitsInterface);
  const [selectedId, setSelectedId] = useState(() => (suitable || translations[0])?.id || '');
  const [mode, setMode] = useState(() => (suitable ? 'translated' : 'structured'));
  const [rawTextOpen, setRawTextOpen] = useState(false);
  const [translatedBackgrounds, setTranslatedBackgrounds] = useState(null);
  useEffect(() => {
    if (translations.length > 0) {
      const matchingNew = newTranslationId && translations.find((item) => item.id === newTranslationId);
      if (matchingNew) {
        setSelectedId(matchingNew.id);
        setMode('translated');
      } else if (!selectedId || !translations.some((item) => item.id === selectedId)) {
        setSelectedId(translations[0].id);
      }
    }
  }, [translations, newTranslationId, selectedId]);
  const selected = translations.find((item) => item.id === selectedId) || translations[0] || null;
  const currentSourceText = resumeSourceText(candidate);
  const hasVisualScanText = (candidate.assets || []).some((asset) => asset.kind.startsWith('resume')
    && !asset.extraction?.text?.trim()
    && asset.extraction?.visionOcr?.some((page) => page?.text?.trim()));
  const originalText = cleanResumeDisplayText(hasVisualScanText ? currentSourceText : (selected?.originalText || currentSourceText));
  const displayedText = cleanResumeDisplayText(mode === 'translated' ? selected?.translatedText : originalText);
  const sourceAssets = (candidate.assets || []).filter((asset) =>
    (selected?.assetIds?.length ? selected.assetIds.includes(asset.id) : asset.kind.startsWith('resume'))
  );
  const structuredDocuments = sourceAssets
    .map((asset) => ({ asset, structure: buildResumeStructureIndex(asset.extraction || {}) }))
    .filter(({ structure }) => structure.sections?.length);
  const hasStructuredOriginal = structuredDocuments.length > 0 && structuredDocuments.length === sourceAssets.length;
  // Field-level translations carry the parsed structure with them, so the
  // translated view renders the original layout instead of re-deriving it from
  // text — which is what made the layout depend on the model's word choices.
  const storedTranslation = mode === 'translated' ? (selected?.structure?.documents || []) : [];
  const legacyTranslatedStructure = mode === 'translated' && !storedTranslation.length && displayedText
    ? buildResumeStructureIndex({ text: displayedText })
    : null;
  const translatedDocuments = storedTranslation.length
    ? storedTranslation.map((document) => ({
      asset: { id: document.assetId || selected?.id || 'translated-resume', originalName: document.assetName || 'Translated Resume' },
      structure: document.structure,
    }))
    : legacyTranslatedStructure?.reliable && legacyTranslatedStructure.sections?.length > 1
      ? [{ asset: { id: selected?.id || 'translated-resume', originalName: 'Translated Resume' }, structure: legacyTranslatedStructure }]
      : [];
  const hasStructuredTranslation = translatedDocuments.length > 0;
  const sourceReferences = sourceAssets.map((asset) => ({
    asset,
    href: asset.url || (asset.storedPath ? `/api/candidates/${candidate.id}/assets/${asset.id}/file` : ''),
  })).filter(({ href }) => href);
  const annotations = resumeAnnotations(candidate);
  const companyBackgrounds = candidate.careerAnalysis?.companyBackgrounds || [];
  const profileEducation = candidate.resumeProfile?.education || [];
  // The company note is AI narrative, which the API localizes by interface
  // language. Inside a resume translated into another language that produced one
  // block with two languages in it, so the translated view asks for the narrative
  // in the language the resume was translated into.
  const translationLocale = selected?.targetLang && /^zh/i.test(selected.targetLang) ? 'zh-CN' : 'en';
  const uiLocale = window.localStorage.getItem('talent-locale') || 'en';
  useEffect(() => {
    if (mode !== 'translated' || !selected || translationLocale === uiLocale) {
      setTranslatedBackgrounds(null);
      return;
    }
    let cancelled = false;
    api.candidate(candidate.id, translationLocale)
      .then((localized) => {
        if (!cancelled) setTranslatedBackgrounds(localized.careerAnalysis?.companyBackgrounds || []);
      })
      .catch(() => { if (!cancelled) setTranslatedBackgrounds(null); });
    return () => { cancelled = true; };
  }, [mode, selected?.id, translationLocale, uiLocale, candidate.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!originalText) {
    return <div className="detail-tab-content resume-text-tab"><EmptyState icon={FileText} title="尚无可查看的简历文本" description="请先处理简历文件；解析完成后原文会显示在这里。" /></div>;
  }
  return (
    <div className="detail-tab-content resume-text-tab">
      {newTranslationId && selected?.id === newTranslationId && <Callout tone="success" title="翻译已生成">最新译文已保存，原文仍完整保留。</Callout>}
      <div className="resume-text-toolbar">
        <div className="segmented-control" aria-label="简历主视图">
          <button className={mode === 'structured' ? 'active' : ''} onClick={() => setMode('structured')}>结构化原文</button>
          <button
            className={mode === 'translated' ? 'active' : ''}
            disabled={translations.length === 0}
            onClick={() => {
              setMode('translated');
              if (!selectedId && translations[0]) {
                setSelectedId(translations[0].id);
              }
            }}
          >
            翻译
          </button>
        </div>
        <div className="resume-toolbar-secondary">
          {translations.length > 0 && <label className="translation-history-select"><span>翻译版本</span><select value={selected?.id || ''} onChange={(event) => { setSelectedId(event.target.value); setMode('translated'); }}>{translations.map((item) => <option key={item.id} value={item.id}>{formatDate(item.createdAt, true)} · {languageLabel(item.targetLang)}</option>)}</select></label>}
          <div className="resume-reference-actions">
            <span>参考资料</span>
            <Button variant="ghost" size="sm" icon={FileText} onClick={() => setRawTextOpen(true)}>原始文本</Button>
            {sourceReferences.map(({ asset, href }) => (
              <a className="button button-ghost button-sm resume-source-link" href={href} target="_blank" rel="noreferrer" title={asset.originalName} key={asset.id}>
                <ExternalLink size={14} /><span>{sourceReferences.length > 1 ? asset.originalName : '原始文件 / PDF'}</span>
              </a>
            ))}
          </div>
        </div>
      </div>
      <section className="resume-document panel">
        <header>
          <div><span>{mode === 'translated' ? 'TRANSLATED RESUME' : 'STRUCTURED ORIGINAL'}</span><h2>{mode === 'translated' ? `${languageLabel(selected?.sourceLang)} → ${languageLabel(selected?.targetLang)}` : '结构化简历原文'}</h2></div>
          <div className="resume-document-meta">
            {mode === 'translated' && selected && <><Badge tone="info">{selected.modelId}</Badge><span>{formatDate(selected.createdAt, true)}</span></>}
            <span>{sourceAssets.map((asset) => asset.originalName).join('、') || '简历资产'}</span>
          </div>
        </header>
        <ResumeAnnotationStrip annotations={annotations} />
        {mode === 'structured' && hasStructuredOriginal
          ? <ResumeSemanticDocumentBody documents={structuredDocuments} companyBackgrounds={companyBackgrounds} education={profileEducation} />
          : mode === 'translated' && hasStructuredTranslation
            ? <ResumeSemanticDocumentBody documents={translatedDocuments} companyBackgrounds={translatedBackgrounds || companyBackgrounds} education={profileEducation} />
            : <ResumeDocumentBody text={displayedText || '该翻译版本没有返回文本。'} />}
      </section>
      {!selected && <Callout tone="info" title="当前仅有结构化原文">点击页面顶部“生成翻译”后，翻译会与结构化原文并列保存。</Callout>}
      <Modal
        open={rawTextOpen}
        title="提取的原始文本"
        description={sourceAssets.map((asset) => asset.originalName).join('、') || '简历资产'}
        width="min(920px, 92vw)"
        onClose={() => setRawTextOpen(false)}
        footer={<Button variant="secondary" onClick={() => setRawTextOpen(false)}>关闭</Button>}
      >
        <pre className="resume-raw-reference" data-i18n-ignore>{originalText}</pre>
      </Modal>
    </div>
  );
}

function PortfolioReferences({ candidate }) {
  const seen = new Set();
  const references = (candidate.assets || []).filter((asset) => asset.kind.startsWith('portfolio'))
    .map((asset) => ({
      asset,
      href: asset.url || (asset.storedPath ? `/api/candidates/${candidate.id}/assets/${asset.id}/file` : ''),
    }))
    .filter(({ asset, href }) => {
      const key = asset.url || asset.storedPath || '';
      if (!href || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return (
    <section className="panel portfolio-references">
      <SectionHeader title="作品集（人工查看）" description="仅保留候选人提供的链接或文件，不进入 AI 解析与评分" />
      {references.length ? <div className="portfolio-reference-list">{references.map(({ asset, href }) => (
        <a className="portfolio-reference-item" href={href} target="_blank" rel="noreferrer" key={asset.id}>
          <span>{asset.url ? <Link2 size={17} /> : <FileText size={17} />}</span>
          <div><strong>{asset.originalName || (asset.url ? '作品集链接' : '作品集文件')}</strong><small>{asset.url || '打开候选人提交的原文件'}</small></div>
          <Badge tone="neutral">人工查看</Badge>
          <ExternalLink size={16} />
        </a>
      ))}</div> : <div className="portfolio-reference-empty"><Layers3 size={22} /><div><strong>未提供可访问的作品集</strong><span>候选人提交链接或原文件后会显示在这里</span></div></div>}
      <p className="portfolio-reference-policy">系统不会打开、截图、总结或评价这些材料，也不会让作品集影响候选人分数。</p>
    </section>
  );
}

function CareerTab({ candidate, risks }) {
  const career = candidate.careerAnalysis || {};
  const contextEvidence = career.businessContextEvidence || [];
  const scaleEvidence = career.businessScaleEvidence || [];
  const companyBackgrounds = career.companyBackgrounds || [];
  return (
    <div className="detail-tab-content career-tab">
      <div className="career-status-grid">
        <div><span>发展轨迹</span><p>{career.trajectory || '待评估'}</p></div>
        <div><span>岗位等级匹配</span><p>{career.levelFit || '待评估'}</p></div>
        <div><span>稳定性</span><p>{career.stability || '待评估'}</p></div>
        <div><span>业务环境匹配</span><p>{career.businessContextFit || '历史评估未包含'}</p></div>
        <div><span>业务规模匹配</span><p>{career.businessScaleFit || '历史评估未包含'}</p></div>
      </div>
      <section className="career-business-context">
        <SectionHeader title="历史公司与业务规模" description="逐家公司区分行业、业务类型、规模与信息依据，不按公司名气直接加分" />
        {companyBackgrounds.length ? <div className="company-background-table-wrap"><table className="company-background-table">
          <thead><tr><th>公司 / 职位</th><th>行业与业务类型</th><th>规模级别</th><th>信息依据</th><th>目标岗位关联</th></tr></thead>
          <tbody>{companyBackgrounds.map((item, index) => <tr key={`${item.company}-${item.role}-${index}`}>
            <td><strong>{item.company || '公司未知'}</strong><span>{item.role || '职位未注明'}</span></td>
            <td><strong>{item.industry || '行业未知'}</strong><p>{item.companyOverview || '缺少公司业务描述。'}</p><div>{(item.businessTypes || ['Unknown']).map((type) => <Badge key={type} tone={type === 'Unknown' ? 'warning' : 'info'}>{COMPANY_BUSINESS_LABELS[type] || type}</Badge>)}</div></td>
            <td><Badge tone={item.scaleLevel === 'unknown' ? 'warning' : 'neutral'}>{COMPANY_SCALE_LABELS[item.scaleLevel] || '规模未知'}</Badge><p>{item.scaleBasis || '缺少规模证据。'}</p></td>
            <td><Badge tone={['resume_explicit', 'web_verified'].includes(item.evidenceBasis) ? 'success' : ['unknown', 'ambiguous'].includes(item.evidenceBasis) ? 'warning' : 'neutral'}>{COMPANY_BASIS_LABELS[item.evidenceBasis] || '待核实'}</Badge><span>置信度 {Math.round(Number(item.confidence || 0) * 100)}%</span>{(item.evidence || []).slice(0, 2).map((evidence) => <p key={evidence}>{evidence}</p>)}{(item.webSources || []).slice(0, 3).map((url, sourceIndex) => <a className="company-source-link" href={url} target="_blank" rel="noreferrer" key={url}>来源 {sourceIndex + 1}</a>)}</td>
            <td><p>{item.relevanceToTarget || '尚未判断与目标岗位的关联。'}</p></td>
          </tr>)}</tbody>
        </table></div> : <div className="company-background-empty"><Building2 size={18} /><div><strong>历史评估未包含逐家公司背景</strong><span>AI 重审后，将展示行业、B2B/B2C/DTC/线下等业务类型及规模依据。</span></div></div>}
        <div className="career-context-columns">
          <div><strong>业务环境证据</strong>{contextEvidence.length ? <ul>{contextEvidence.map((item) => <li key={item}>{item}</li>)}</ul> : <p>当前评估没有足够证据。</p>}</div>
          <div><strong>业务规模证据</strong>{scaleEvidence.length ? <ul>{scaleEvidence.map((item) => <li key={item}>{item}</li>)}</ul> : <p>当前评估没有足够证据。</p>}</div>
        </div>
      </section>
      <section className="career-path">
        <SectionHeader title="职业路径判断" description="只评估职责、项目复杂度和岗位层级，不使用年龄" />
        <div className="career-timeline">
          {(career.highlights?.length ? career.highlights : ['等待更多结构化履历证据']).map((highlight, index) => (
            <div key={highlight}><i>{index + 1}</i><span><strong>{highlight}</strong><small>{index === 0 ? '履历与项目证据' : '职责范围变化'}</small></span></div>
          ))}
        </div>
      </section>
      <section><SectionHeader title="需要面试确认" description="风险提示不会触发自动淘汰" /><RiskList risks={risks} /></section>
      <Callout tone="success" title="公平招聘边界">已忽略：{candidate.protectedSignalsIgnored?.join('、') || '所有受保护属性'}。</Callout>
    </div>
  );
}

function HistoryTab({ candidate }) {
  const purposeLabels = {
    candidate_scoring: '候选人审核', company_research: '公司背景搜索', portfolio_vision: '历史作品集评审（已停用）',
    vision_ocr: '简历 OCR', translation: '简历翻译', assessment_localization: '诊断内容翻译',
  };
  return (
    <div className="detail-tab-content history-tab">
      <SectionHeader title="招聘阶段记录" description="人工阶段变更、面试安排和通知任务" />
      {(candidate.workflowEvents || []).length === 0 ? <EmptyState icon={Clock3} title="尚无招聘阶段变更" description="首次复核后会在这里形成完整时间线。" /> : <div className="workflow-event-list">{candidate.workflowEvents.map((event) => (
        <article key={event.id}><span><Check size={15} /></span><div><strong>{workflowStatusLabel(event.fromStatus)} → {workflowStatusLabel(event.toStatus)}</strong><p>{event.note || '未填写流转备注'}</p><small>{event.actorName || '系统'} · {formatDate(event.createdAt, true)}</small></div></article>
      ))}</div>}
      {(candidate.interviews || []).length > 0 && <><SectionHeader title="面试安排" description="已确认的面试时间" /><div className="interview-history-list">{candidate.interviews.map((interview) => <article key={interview.id}><CalendarDays size={17} /><div><strong>{formatDate(interview.scheduledAt, true)} · {interview.durationMinutes} 分钟</strong><span>{interview.meetingMode === 'teams' ? 'Microsoft Teams' : interview.meetingMode === 'in_person' ? '线下面试' : '视频面试'}{interview.location ? ` · ${interview.location}` : ''}</span></div><Badge tone="info">{interview.status === 'scheduled' ? '已安排' : interview.status}</Badge></article>)}</div></>}
      {(candidate.notifications || []).length > 0 && <><SectionHeader title="通知与日历任务" description="邮件与 Microsoft Graph 交付状态" /><div className="notification-list">{candidate.notifications.map((item) => <article key={item.id}><span>{item.type === 'teams_calendar' ? <CalendarPlus size={16} /> : <Mail size={16} />}</span><div><strong>{item.subject}</strong><small>{item.recipientName || item.recipientEmail || 'Teams Calendar'} · {formatDate(item.createdAt, true)}</small></div><Badge tone={item.status === 'sent' ? 'success' : item.status === 'failed' ? 'danger' : 'warning'}>{item.status === 'pending_configuration' ? '待配置' : item.status}</Badge></article>)}</div></>}
      <SectionHeader title="评分校准" description="人工调整分与复核备注" />
      {(candidate.calibrations || []).length === 0 ? <EmptyState icon={MessageSquareText} title="尚无人工校准记录" description="首次复核后，人工分数、结论和备注会记录在这里。" /> : (
        <div className="history-list">{candidate.calibrations.map((item) => (
          <article key={item.id}><span><Check size={17} /></span><div><strong>{item.reviewer} · {item.decision}</strong><p>{item.notes || '未填写备注'}</p><small>{formatDate(item.createdAt, true)}</small></div><div><span>{item.previousScore ?? '—'}</span><ChevronRight size={14} /><strong>{item.adjustedScore ?? '—'}</strong></div></article>
        ))}</div>
      )}
      <SectionHeader title="评分版本" description="每次 AI 重评与人工调整均保留快照" />
      <div className="version-list">{(candidate.scoreVersions || []).map((version) => <div key={version.id}><span>{version.version}</span><strong>{version.reason}</strong><small>{formatDate(version.createdAt, true)}</small></div>)}</div>
      <SectionHeader title="AI 运行与上下文" description="功能调用、模型、版本和实际上下文范围" />
      {(candidate.aiRuns || []).length === 0 ? <EmptyState icon={Activity} title="尚无 AI Run" description="候选人执行模型任务后会在这里生成独立上下文记录。" /> : <div className="ai-run-list">{candidate.aiRuns.map((run) => (
        <article key={run.id}>
          <span><Activity size={16} /></span>
          <div><strong>{purposeLabels[run.purpose] || run.purpose}</strong><code>{run.providerName || '未解析 Provider'} / {run.modelId || '未选择模型'}</code><small>{run.promptVersion} · {run.contextPolicyVersion}</small></div>
          <div><Badge tone={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'danger' : 'info'}>{run.status}</Badge>{run.fallbackFromModelId && <Badge tone="warning">Fallback</Badge>}</div>
          <div><strong>{run.contextSources?.length || 0}</strong><small>来源</small></div>
          <div><strong>{run.contextStats?.estimatedTotalTokens || run.contextStats?.estimatedTokens || 0}</strong><small>估算 tokens</small></div>
          <time>{formatDate(run.startedAt, true)}</time>
        </article>
      ))}</div>}
    </div>
  );
}

function resumeOnlyDimensions(dimensions = []) {
  const active = dimensions.filter((item) => item.key !== 'portfolio');
  if (active.length === dimensions.length || !active.length) return active;
  const totalWeight = active.reduce((sum, item) => sum + Number(item.weight || 0), 0);
  if (!totalWeight) return active;
  let assigned = 0;
  return active.map((item, index) => {
    const weight = index === active.length - 1 ? 100 - assigned : Math.round((Number(item.weight || 0) / totalWeight) * 100);
    assigned += weight;
    return { ...item, weight };
  });
}

function containsLegacyPortfolioJudgment(value) {
  return /portfolio|作品集|作品质量|作品原型|案例页|高保真|渲染图|mockup|视觉(?:能力|完成度|判断|复核)/i.test(JSON.stringify(value || ''));
}

const WORKFLOW_STAGES = [
  { key: 'review', label: '复核', statuses: ['needs_review', 'backup'] },
  { key: 'recommended', label: '推荐', statuses: ['recommended'] },
  { key: 'interview', label: '面试', statuses: ['scheduling', 'interview_scheduled'] },
  { key: 'offer', label: 'Offer', statuses: ['offer_ready', 'offer_sent', 'offer_accepted'] },
  { key: 'hired', label: '入职', statuses: ['hired'] },
];

const WORKFLOW_ACTIONS = {
  needs_review: { label: '打回复核', icon: Undo2 },
  backup: { label: '设为备选', icon: Bookmark },
  recommended: { label: '推荐面试', icon: UserCheck },
  scheduling: { label: '开始约面', icon: Phone },
  offer_ready: { label: '建议发 Offer', icon: UserCheck },
  offer_sent: { label: '确认已发 Offer', icon: Send },
  offer_accepted: { label: '记录接受 Offer', icon: Check },
  offer_declined: { label: '记录拒绝 Offer', icon: XCircle },
  hired: { label: '确认已入职', icon: BadgeCheck },
  not_match: { label: '不匹配', icon: XCircle },
};

function WorkflowStageBar({ status }) {
  const currentIndex = WORKFLOW_STAGES.findIndex((stage) => stage.statuses.includes(status));
  const closed = ['not_match', 'offer_declined'].includes(status);
  return (
    <div className="workflow-stage-wrap">
      <div className="workflow-stage-bar">
        {WORKFLOW_STAGES.map((stage, index) => (
          <div className={`${!closed && index < currentIndex ? 'complete' : ''} ${!closed && index === currentIndex ? 'active' : ''}`} key={stage.key}>
            <i>{index < currentIndex && !closed ? <Check size={13} /> : index + 1}</i><span>{stage.label}</span>
          </div>
        ))}
      </div>
      {closed && <span className="workflow-closed"><XCircle size={15} />流程已关闭 · {workflowStatusLabel(status)}</span>}
    </div>
  );
}

function WorkflowPanel({ candidate, state, busy, onTransition, onSchedule }) {
  const status = candidate.workflowStatus || 'needs_review';
  const allowed = state?.workflow?.allowedTransitions?.[status] || [];
  const latestInterview = candidate.interviews?.[0];
  const pendingNotifications = (candidate.notifications || []).filter((item) => item.status === 'pending_configuration').length;
  return (
    <section className="candidate-workflow-panel">
      <div className="workflow-panel-head">
        <div><span>招聘阶段</span><Badge tone={workflowStatusTone(status)}>{workflowStatusLabel(status)}</Badge>{candidate.workflowEvents?.[0] && <small>{candidate.workflowEvents[0].actorName} · {formatDate(candidate.workflowEvents[0].createdAt, true)}</small>}</div>
        <WorkflowStageBar status={status} />
      </div>
      <div className="workflow-panel-actions">
        {allowed.filter((target) => target !== 'interview_scheduled').map((target) => {
          const action = WORKFLOW_ACTIONS[target];
          if (!action) return null;
          return <Button key={target} size="sm" icon={action.icon} variant={target === 'recommended' || target === 'scheduling' || target === 'offer_sent' || target === 'hired' ? 'primary' : target === 'not_match' || target === 'offer_declined' ? 'danger-ghost' : 'secondary'} loading={busy === target} disabled={Boolean(busy)} onClick={() => onTransition(target)}>{action.label}</Button>;
        })}
        {allowed.includes('interview_scheduled') && <Button size="sm" icon={CalendarPlus} variant="primary" disabled={Boolean(busy)} onClick={onSchedule}>登记面试时间</Button>}
      </div>
      {(latestInterview || pendingNotifications > 0) && <div className="workflow-operational-meta">
        {latestInterview && <span><CalendarDays size={14} />{formatDate(latestInterview.scheduledAt, true)} · {latestInterview.durationMinutes} 分钟</span>}
        {pendingNotifications > 0 && <span><Mail size={14} />{pendingNotifications} 个通知/日历任务待 Graph 配置</span>}
      </div>}
    </section>
  );
}

function localDateTimeValue(date = new Date(Date.now() + 24 * 60 * 60 * 1000)) {
  const value = new Date(date);
  value.setHours(10, 0, 0, 0);
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 16);
}

function ScheduleInterviewModal({ candidate, state, open, onClose, onSaved, notify }) {
  const assignedHiringManagers = (state?.users || []).filter((user) => user.active
    && user.role === 'hiring_manager' && user.jobIds?.includes(candidate.jobId));
  const interviewers = assignedHiringManagers.length
    ? assignedHiringManagers
    : (state?.users || []).filter((user) => user.active && user.role === 'admin');
  const [scheduledAt, setScheduledAt] = useState(localDateTimeValue);
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [meetingMode, setMeetingMode] = useState('teams');
  const [location, setLocation] = useState('');
  const [interviewerIds, setInterviewerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setScheduledAt(localDateTimeValue());
    setDurationMinutes('60');
    setMeetingMode('teams');
    setLocation('');
    setInterviewerIds(interviewers[0] ? [interviewers[0].id] : []);
  }, [open, candidate.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleInterviewer = (id) => setInterviewerIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  const save = async () => {
    if (!scheduledAt || !interviewerIds.length) return notify('请选择面试时间和至少一位面试官', 'danger');
    setSaving(true);
    try {
      const result = await api.scheduleInterview(candidate.id, {
        actorId: state.currentUser.id,
        scheduledAt: new Date(scheduledAt).toISOString(),
        durationMinutes: Number(durationMinutes), meetingMode, location, interviewerIds,
      });
      onSaved(result);
      notify('面试已登记，邮件与 Teams 日历任务已创建');
      onClose();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`登记面试 · ${candidate.displayName}`} description="候选人时间确认后，再创建面试官通知与 Teams 日历任务。" footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" icon={CalendarPlus} loading={saving} onClick={save}>确认面试安排</Button></>}>
      <div className="schedule-interview-form">
        <div className="form-grid two">
          <label><span>面试时间</span><input type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} /></label>
          <label><span>时长</span><select value={durationMinutes} onChange={(event) => setDurationMinutes(event.target.value)}><option value="30">30 分钟</option><option value="45">45 分钟</option><option value="60">60 分钟</option><option value="90">90 分钟</option></select></label>
          <label><span>形式</span><select value={meetingMode} onChange={(event) => setMeetingMode(event.target.value)}><option value="teams">Microsoft Teams</option><option value="video">其他视频会议</option><option value="in_person">线下面试</option></select></label>
          <label><span>地点 / 会议说明</span><input value={location} onChange={(event) => setLocation(event.target.value)} placeholder={meetingMode === 'teams' ? 'Teams 会议' : '办公室或会议链接'} /></label>
        </div>
        <div className="interviewer-picker"><span>面试官</span><div>{interviewers.map((user) => <label key={user.id} className={interviewerIds.includes(user.id) ? 'selected' : ''}><input type="checkbox" checked={interviewerIds.includes(user.id)} onChange={() => toggleInterviewer(user.id)} /><Avatar name={user.name} size="sm" /><span><strong>{user.name}</strong><small>{user.title} · {user.email}</small></span></label>)}</div></div>
        <Callout tone="info" title="通知与日历">面试保存后生成邮件和 Teams Calendar 任务；在 Microsoft Graph 配置完成前保持待发送状态。</Callout>
      </div>
    </Modal>
  );
}

function CalibrationModal({ candidate, open, onClose, onSaved, notify }) {
  const [score, setScore] = useState(getCandidateScore(candidate) ?? '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setScore(getCandidateScore(candidate) ?? '');
    setNotes('');
  }, [candidate, open]);
  const save = async () => {
    setSaving(true);
    try {
      const result = await api.calibrate(candidate.id, { adjustedScore: score, decision: '评分校准', notes, reviewer: '当前复核人' });
      notify('人工校准已保存并进入审计记录');
      onSaved(result);
      onClose();
    } catch (error) { notify(error.message, 'danger'); }
    finally { setSaving(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title={`人工校准 · ${candidate.displayName}`} description="校准只影响排序分；招聘阶段请在候选人流程中单独更新。" footer={<><Button onClick={onClose}>取消</Button><Button variant="primary" loading={saving} onClick={save}>保存校准</Button></>}>
      <div className="calibration-form">
        <label><span>人工调整分</span><div className="score-input"><input type="number" min="0" max="100" value={score} onChange={(event) => setScore(event.target.value)} /><strong>/ 100</strong></div></label>
        <label><span>复核备注</span><textarea rows="5" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="记录关键证据、不同意 AI 的原因或后续面试问题" /></label>
      </div>
    </Modal>
  );
}

export default function CandidateDetail({ candidateId, returnJobId, state, navigate, refresh, notify, locale }) {
  const [candidate, setCandidate] = useState(() => state?.candidates?.find((item) => item.id === candidateId) || null);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('overview');
  const [calibrationOpen, setCalibrationOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [workflowBusy, setWorkflowBusy] = useState('');
  const [newTranslationId, setNewTranslationId] = useState('');
  const [translationTaskId, setTranslationTaskId] = useState('');
  const [aiSetupOpen, setAiSetupOpen] = useState(false);

  useEffect(() => {
    let active = true;
    setCandidate(state?.candidates?.find((item) => item.id === candidateId) || null);
    setLoadError('');
    api.candidate(candidateId, locale).then((data) => active && setCandidate(data)).catch((error) => {
      if (!active) return;
      setLoadError(error.status === 403 ? '你没有权限查看该部门的简历。' : error.message);
      notify(error.message, 'danger');
    });
    return () => { active = false; };
  }, [candidateId, locale, notify]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!candidate && loadError) return <div className="page detail-loading"><EmptyState icon={XCircle} title="无法查看候选人" description={loadError} action={<Button onClick={() => navigate('candidates', returnJobId || null)}>返回候选人列表</Button>} /></div>;
  if (!candidate) return <div className="page detail-loading"><Skeleton rows={8} /></div>;
  const score = getCandidateScore(candidate);
  const confidence = getCandidateConfidence(candidate);
  const recommendation = getCandidateRecommendation(candidate);
  const assessmentUnstarted = candidate.status === 'ready';
  const legacyPortfolioAssessment = (candidate.dimensions || []).some((item) => item.key === 'portfolio');
  const designRole = isDesignJobTitle(candidate.jobTitle);
  const activeDimensions = resumeOnlyDimensions(candidate.dimensions || []);
  const topStrengths = [...activeDimensions].sort((a, b) => b.score - a.score).slice(0, 3);
  const activeRisks = legacyPortfolioAssessment
    ? (candidate.risks || []).filter((risk) => !containsLegacyPortfolioJudgment(risk))
    : (candidate.risks || []);
  const activeMissingInfo = legacyPortfolioAssessment
    ? (candidate.missingInfo || []).filter((item) => !containsLegacyPortfolioJudgment(item))
    : (candidate.missingInfo || []);
  const fallbackAssessmentSummary = legacyPortfolioAssessment
    ? '该候选人的旧版摘要包含已停用的作品集评估，当前不再展示。下方分数已排除作品集维度；AI 重审后会生成仅基于简历证据的新版摘要。'
    : assessmentUnstarted
      ? '材料已归档，尚未开始处理。点击“开始评估”后，系统会先解析简历，再调用已配置的模型生成评估。'
      : '系统正在处理或等待评分结果。';
  const assessmentSummary = candidate.summary || translateUiText(fallbackAssessmentSummary, locale);
  const assessmentModel = assessmentModelLabel(candidate);
  const sourceResumeText = resumeSourceText(candidate);
  const hasResumeText = Boolean(sourceResumeText.trim());
  const chineseCharacters = (sourceResumeText.match(/[\u3400-\u9fff]/g) || []).length;
  const translationTarget = chineseCharacters >= Math.max(10, sourceResumeText.length * 0.04) ? 'en' : 'zh';
  const assessmentModelReady = Boolean(state?.system?.aiReadiness?.assessment ?? state?.system?.aiReadiness?.scoring);

  const translationTask = translationTaskId
    ? (state?.tasks || []).find((task) => task.id === translationTaskId)
    : (state?.tasks || []).find((task) => task.type === 'resume_translation'
      && task.candidateId === candidateId && ['queued', 'processing'].includes(task.status));
  const translationRunning = ['queued', 'processing'].includes(translationTask?.status || '');

  useEffect(() => {
    if (!translationTaskId || !translationTask) return;
    if (['queued', 'processing'].includes(translationTask.status)) return;
    setTranslationTaskId('');
    if (translationTask.status === 'completed') {
      api.candidate(candidateId, locale).then((fresh) => {
        setCandidate(fresh);
        const latest = (fresh.translations || [])[0];
        if (latest) setNewTranslationId(latest.id);
        notify('简历译文已生成');
      }).catch((error) => notify(error.message, 'danger'));
    } else if (translationTask.status === 'failed') {
      notify(translationTask.errorMessage || translationTask.message || '简历翻译失败', 'danger');
    }
  }, [translationTask, translationTaskId, candidateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAction = async (type) => {
    if (type === 'process' && !assessmentModelReady) {
      setAiSetupOpen(true);
      return;
    }
    setActionLoading(type);
    try {
      if (type === 'process') {
        await api.processCandidate(candidate.id);
        notify('候选人已进入系统处理队列');
        await refresh();
        setCandidate(await api.candidate(candidate.id, locale));
      } else if (type === 'cancel') {
        await api.cancelCandidateProcessing(candidate.id);
        notify('已取消 AI 评估');
        await refresh();
        setCandidate(await api.candidate(candidate.id, locale));
      } else if (type === 'translate') {
        const task = await api.translate(candidate.id, translationTarget);
        setTranslationTaskId(task.id);
        setTab('resume');
        notify('简历翻译已进入任务队列');
        await refresh();
      }
    } catch (error) { notify(error.message, 'danger'); }
    finally { setActionLoading(''); }
  };

  const transitionWorkflow = async (toStatus) => {
    setWorkflowBusy(toStatus);
    try {
      const result = await api.transitionCandidate(candidate.id, {
        actorId: state.currentUser.id,
        toStatus,
      });
      setCandidate(result);
      await refresh();
      const notificationNote = toStatus === 'recommended' && state.currentUser.role === 'hiring_manager'
        ? '，已生成通知人事任务' : '';
      notify(`招聘阶段已更新为“${workflowStatusLabel(toStatus)}”${notificationNote}`);
    } catch (error) { notify(error.message, 'danger'); }
    finally { setWorkflowBusy(''); }
  };

  return (
    <div className="page candidate-detail-page">
      <button className="back-link" onClick={() => navigate('candidates', returnJobId)}><ArrowLeft size={16} />返回候选人排名</button>
      <header className="candidate-hero">
        <div className="candidate-hero-identity"><Avatar name={candidate.displayName} size="xl" /><div><div><h1>{candidate.displayName}</h1><Badge tone={workflowStatusTone(candidate.workflowStatus)}>{workflowStatusLabel(candidate.workflowStatus)}</Badge>{candidate.source === 'demo' && <Badge tone="neutral">演示</Badge>}</div><p>{candidate.jobTitle} · {candidate.assets?.length || candidate.assetCount} 份材料 · {statusLabel(candidate.status)}</p></div></div>
        <div className="candidate-hero-score"><ScoreRing score={score} size={76} /><div><span>AI 参考</span><Badge tone={recommendation === '不匹配' ? 'danger' : recommendation === '推荐面试' ? 'success' : recommendation === '继续复核' ? 'warning' : scoreTone(score)}>{recommendation}</Badge><Confidence value={confidence} /></div></div>
        <div className="candidate-hero-actions">
          {['queued', 'processing'].includes(candidate.status) ? (
            <Button
              variant="secondary"
              icon={XCircle}
              loading={actionLoading === 'cancel'}
              disabled={Boolean(actionLoading) && actionLoading !== 'cancel'}
              onClick={() => runAction('cancel')}
            >
              取消评估
            </Button>
          ) : (
            <Button
              icon={assessmentUnstarted ? CirclePlay : RefreshCw}
              loading={actionLoading === 'process'}
              disabled={Boolean(actionLoading)}
              onClick={() => runAction('process')}
            >
              {assessmentUnstarted ? '开始评估' : 'AI重审'}
            </Button>
          )}
          <Button
            icon={Languages}
            loading={actionLoading === 'translate' || translationRunning}
            disabled={Boolean(actionLoading) || translationRunning || !hasResumeText}
            title={!hasResumeText ? '请先处理并解析简历文本' : `翻译为${languageLabel(translationTarget)}`}
            onClick={() => runAction('translate')}
          >
            {translationRunning ? '翻译进行中' : candidate.translations?.length ? '重新翻译' : '生成翻译'}
          </Button>
          {state.currentUser.role !== 'hr' && (
            <Button
              variant="primary"
              icon={Scale}
              disabled={Boolean(actionLoading)}
              onClick={() => setCalibrationOpen(true)}
            >
              人工校准
            </Button>
          )}
        </div>
      </header>

      <WorkflowPanel candidate={candidate} state={state} busy={workflowBusy} onTransition={transitionWorkflow} onSchedule={() => setScheduleOpen(true)} />

      <nav className="detail-tabs">{TABS.map((item) => <button key={item.id} className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}><item.icon size={16} />{item.label}</button>)}</nav>

      {tab === 'overview' && (
        <div className="detail-tab-content detail-overview detail-overview-layout">
          <div className="detail-overview-main">
            {candidate.assessmentLocalization?.fallback && <Callout tone="warning" title="AI 诊断语言包尚未生成">当前语言的 AI 诊断尚未生成，暂时显示原语言内容。</Callout>}
            <section className="assessment-summary">
              <div className="panel"><SectionHeader title="评估摘要" description="仅基于简历原文与结构化证据" /><p className="summary-copy" data-i18n-ignore>{assessmentSummary}</p><div className="strength-chips">{topStrengths.map((item) => <Badge key={item.key} tone="success" data-i18n-ignore><Check size={12} />{item.label} {Math.round(item.score)}</Badge>)}</div>{activeMissingInfo.length > 0 && <div className="missing-list"><div><CircleHelp size={16} /><span>待补信息</span></div><ul>{activeMissingInfo.slice(0, 6).map((item) => <li key={item} data-i18n-ignore>{item}</li>)}</ul>{activeMissingInfo.length > 6 && <small>另有 {activeMissingInfo.length - 6} 项，建议在面试前统一确认。</small>}</div>}{assessmentModel && <div className="assessment-model-note"><span>审核与评分模型</span><code>{assessmentModel}</code></div>}</div>
              <div className="panel potential-assessment-panel"><PotentialPanel candidate={candidate} locale={locale} /></div>
            </section>
            <section className="panel dimension-panel"><SectionHeader title="岗位维度评分" description="简历证据维度" /><DimensionRows dimensions={activeDimensions} /></section>
            <section className={`assessment-bottom${designRole ? '' : ' assessment-bottom-single'}`}>
              <div className="panel"><SectionHeader title="风险与面试问题" description="提示人工确认，不触发自动拒绝" /><RiskList risks={activeRisks} pending={assessmentUnstarted || candidate.status === 'queued' || candidate.status === 'processing'} /></div>
              {designRole && <PortfolioReferences candidate={candidate} />}
            </section>
          </div>
          <aside className="detail-overview-aside"><ResumeProfilePanel candidate={candidate} /></aside>
        </div>
      )}
      {tab === 'resume' && <ResumeTextTab candidate={candidate} newTranslationId={newTranslationId} />}
      {tab === 'career' && <CareerTab candidate={candidate} risks={activeRisks} />}
      {tab === 'history' && <HistoryTab candidate={candidate} />}

      <CalibrationModal candidate={candidate} open={calibrationOpen} onClose={() => setCalibrationOpen(false)} notify={notify} onSaved={(result) => { setCandidate(result); refresh(); }} />
      <ScheduleInterviewModal candidate={candidate} state={state} open={scheduleOpen} onClose={() => setScheduleOpen(false)} notify={notify} onSaved={(result) => { setCandidate(result); refresh(); }} />
      <Modal open={aiSetupOpen} title="需要先配置 AI Provider" description="当前没有可用于简历审核的 AI Provider 和模型。" onClose={() => setAiSetupOpen(false)} footer={<><Button variant="ghost" onClick={() => setAiSetupOpen(false)}>关闭</Button>{state.currentUser.role === 'admin' && <Button variant="primary" icon={Settings2} onClick={() => navigate('models')}>前往模型与处理</Button>}</>}>
        <Callout tone="warning" title="AI 处理尚未启动">请为“审核模型”选择已启用的 Provider 和 Model，并配置 API Base URL 与 API Key。{state.currentUser.role !== 'admin' ? '请联系管理员完成配置。' : ''}</Callout>
      </Modal>
    </div>
  );
}
